import { from, of } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { RootEpic } from '~/Store/types'
import { errorNotification, ProductService } from '~/Services'
import { RolesActions } from './slice'
import { RolesService } from '~/Services/AdminRolesService'
import { AdminUserService } from '~/Services/AdminUsers'
import { successNotification } from '~/Services/utils'

const getAllRoles: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(RolesActions.getRolesRequest.match),
    switchMap(action => {
      return from(RolesService.Get.getAllRoles(action.payload)).pipe(
        map(res => RolesActions.setRolesSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return of(RolesActions.setRolesError())
        }),
      )
    }),
  )

export const getUserRolesEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.getUserRolesRequest.match),
    mergeMap(action => {
      const userId = action.payload
      return from(AdminUserService.Get.getRolesToUserId(userId)).pipe(
        map(res =>
          RolesActions.getUserRolesSuccess({ userId, roles: res.data }),
        ),
        catchError(error => {
          errorNotification(error)
          return of(
            RolesActions.getUserRolesFailure(
              error.message || 'Lỗi khi lấy danh sách vai trò',
            ),
          )
        }),
      )
    }),
  )

const updateRoleEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.updateRoleRequest.match),
    switchMap(action => {
      const { type, ...data } = action.payload
      return from(RolesService.Put.updateRolesInfo(data)).pipe(
        concatMap(() => [
          RolesActions.updateRoleSuccess(),
          RolesActions.getRolesRequest(type),
        ]),
        catchError(error => {
          errorNotification(error)
          return of(RolesActions.updateRoleError())
        }),
      )
    }),
  )

const deleteRoles: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.deleteRoleRequest.match),
    switchMap(({ payload }) =>
      from(RolesService.Delete.removeRoles(payload.roleName)).pipe(
        concatMap(() => [
          RolesActions.deleteRoleSuccess(),
          RolesActions.getRolesRequest(payload.type),
        ]),
        catchError(error => {
          errorNotification(error)
          return [RolesActions.deleteRoleError()]
        }),
      ),
    ),
  )

const createRoleEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.addRolesRequest.match),
    switchMap(action => {
      const { roleName, type } = action.payload
      return from(RolesService.Post.addRoles(roleName, type)).pipe(
        concatMap(() => [
          RolesActions.addRoleSuccess(),
          RolesActions.getRolesRequest(type),
        ]),
        catchError(error => {
          errorNotification(error)
          return [RolesActions.addRoleError()]
        }),
      )
    }),
  )

// Roles aren't scoped to a Service Principal, so permissions are picked from
// each product's full claim catalog (`products/{id}/claims`), not the
// SP-scoped `service-principals/{id}/claims` used by ApiKey creation.
const getProductClaimsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.getProductClaims.match),
    // mergeMap: the Edit Role screen dispatches one of these per product
    // synchronously in a loop — switchMap would cancel every request but the
    // last one before its response ever arrives (see Store/ApiKey/epics.ts).
    mergeMap(action => {
      const productId = action.payload
      return from(ProductService.Get.getProductClaimsById(productId)).pipe(
        map(res =>
          RolesActions.getProductClaimsSuccess({ productId, claims: res.data }),
        ),
        catchError(error => {
          errorNotification(error)
          return [RolesActions.getProductClaimsFail(productId)]
        }),
      )
    }),
  )

const getRoleClaimsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.getRoleClaims.match),
    switchMap(action => {
      const roleId = action.payload
      return from(RolesService.Get.getRoleClaims(roleId)).pipe(
        map(res => RolesActions.getRoleClaimsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [RolesActions.getRoleClaimsFail()]
        }),
      )
    }),
  )

const saveRoleEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.saveRole.match),
    switchMap(action => {
      const { roleId, roleName, newRolename, type, productClaimIds } =
        action.payload
      const rename$ =
        newRolename !== roleName
          ? from(RolesService.Put.updateRolesInfo({ roleName, newRolename }))
          : of(null)
      return rename$.pipe(
        switchMap(() =>
          from(RolesService.Put.replaceRoleClaims(roleId, productClaimIds)),
        ),
        concatMap(() => [
          RolesActions.saveRoleSuccess(),
          RolesActions.getRolesRequest(type),
        ]),
        catchError(error => {
          errorNotification(error)
          return [RolesActions.saveRoleFail()]
        }),
      )
    }),
  )

const updateRolesUserEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.updateUserRoles.match),
    mergeMap(action => {
      const { userId, roleName } = action.payload
      //console.log('Updating roles for user:', action.payload)
      return from(AdminUserService.Put.updateRolesUser(userId, { roleName })).pipe(
        map(() => {
          successNotification('Cập nhật vai trò thành công!')
          return RolesActions.getUserRolesRequest(userId)
        }),
        catchError(error => {
          errorNotification(error)
          return of(
            RolesActions.updateUserRoleFailure(
              error.message || 'Error updating user role',
            ),
          )
        }),
      )
    }),
  )

const deleteUserRoleEpic: RootEpic = action$ =>
  action$.pipe(
    filter(RolesActions.deleteUserRolesRequest.match),
    mergeMap(action => {
      const { userId, roleName } = action.payload
      return from(
        AdminUserService.Delete.deleteRolesUser(userId, roleName), // gửi mảng chuỗi
      ).pipe(
        map(() => {
          successNotification(
            `Xóa vai trò "${roleName.join(', ')}" thành công!`,
          )
          return [
            //RolesActions.deleteUserRolesSuccess({ userId, roleName }),
            RolesActions.getUserRolesRequest(userId),
          ]
        }),
        mergeMap(actions => from(actions)),
        catchError(error => {
          errorNotification(error)
          return of(
            RolesActions.deleteUserRolesFailure(
              error.message || 'Lỗi khi xóa vai trò người dùng',
            ),
          )
        }),
      )
    }),
  )

export const RolesEpics = [
  getAllRoles,
  updateRolesUserEpic,
  getUserRolesEpic,
  deleteUserRoleEpic,
  createRoleEpic,
  updateRoleEpic,
  deleteRoles,
  getProductClaimsEpic,
  getRoleClaimsEpic,
  saveRoleEpic,
]
